import { Box, Tooltip, Typography, Skeleton } from '@mui/material';
import { Calendar, Diagram, Graph, Moneys, Receive, Transmit, Warning2 } from 'iconsax-react';
import type { FC, ReactElement } from 'react';

import { abbreviateNumber } from '@core/utils/decimals';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { SlabCard, TypographyWithTooltip } from '@web/components';
import { ValueToNumberString } from '@web/components/DataTable/TableDataModel';
import { EMPTY_LINE_CHAR } from '@web/constants/app';
import { palette } from '@web/theme/palette';

import { type MetricItem, MetricType } from '../../../utils/backlog';

export const cardColor: { [key in MetricType]: string } = {
  [MetricType.Volume]: palette.SlabPurple['100'],
  [MetricType.Forecast]: palette.SlabIndigo['500'],
  [MetricType.Backlog]: palette.SlabTurquoise['500'],
  [MetricType.Actual]: palette.SlabGreen.Status,
  [MetricType.AtRisk]: palette.SlabYellow['800'],
};

export const cardIcon: { [key in MetricType]: ReactElement } = {
  [MetricType.Volume]: <Moneys variant='TwoTone' />,
  [MetricType.Forecast]: <Graph variant='TwoTone' />,
  [MetricType.Backlog]: <Diagram variant='TwoTone' />,
  [MetricType.Actual]: <Calendar variant='TwoTone' />,
  [MetricType.AtRisk]: <Warning2 variant='TwoTone' />,
};

export const getStatusIcon = (status: string): ReactElement | null => {
  const match = status.match(/(-?\d+(?:\.\d+)?)%/);
  if (!hasValue(match)) {
    return <Warning2 size={16} variant='TwoTone' />;
  }

  const value = parseFloat(match[1]);
  if (value > 0) {
    return <Transmit size={16} variant='TwoTone' />;
  }
  if (value < 0) {
    return <Receive size={16} variant='TwoTone' />;
  }
  return null;
};

export const getStatusColor = (status: string): string => {
  if (status.includes('-')) {
    return 'red ';
  }
  if (status.includes('+')) {
    return 'green';
  }
  return 'primary';
};

type Props = {
  isLoading: boolean;
  title: string;
  subTitle?: string;
  type: MetricType;
  noAbbreviate?: boolean;
  infoTooltip?: string;
  metricItems: MetricItem[];
};

const metricCardSkeletonRows = [1, 2];

export const MetricCard: FC<Props> = ({
  isLoading,
  title,
  type,
  subTitle,
  noAbbreviate,
  infoTooltip,
  metricItems,
}) => {
  const renderValue = (value: MetricItem['value']): string => {
    if (!hasStringValue(value) || value === '-') {
      return '-';
    }
    return noAbbreviate ? value : abbreviateNumber(value);
  };

  const filteredMetricItems = metricItems.filter((item) => hasValue(item.value));

  return (
    <SlabCard
      sx={{
        alignItems: 'center',
        borderRadius: '12px',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
        padding: '10px',
        paddingLeft: '12px',
        borderLeft: `4px solid ${cardColor[type]}`,
        marginY: 0,
        height: '100%',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Box display='flex' justifyContent='space-between' alignItems='flex-start' sx={{ mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }} mr={1}>
            <TypographyWithTooltip variant='h5' text={title}>
              {title}
            </TypographyWithTooltip>
            <Typography
              variant='body3'
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minHeight: '1.2em',
              }}
            >
              {subTitle ?? EMPTY_LINE_CHAR}
            </Typography>
          </Box>
          <Tooltip title={infoTooltip}>
            <Box>{cardIcon[type]}</Box>
          </Tooltip>
        </Box>

        {isLoading && (
          <>
            {metricCardSkeletonRows.map((key) => (
              <Box
                key={key}
                display='flex'
                gap={1}
                justifyContent='space-between'
                alignItems='flex-end'
                sx={{
                  borderBottom: `1px solid ${palette.SlabGray.Stroke}`,
                  mb: 1,
                }}
              >
                <Box display='flex' alignItems='baseline' gap={0.5} sx={{ mb: 1, flexShrink: 0 }}>
                  <Skeleton variant='text' width={80} height={29} />
                  <Skeleton variant='text' width={28} height={18} />
                </Box>

                <Skeleton variant='text' width={56} height={16} sx={{ mb: 1 }} />
              </Box>
            ))}
          </>
        )}
        {!isLoading && filteredMetricItems.length === 0 && (
          <Box>
            <Typography variant='body1' color='text.secondary' sx={{ fontWeight: 500 }}>
              N/A
            </Typography>
          </Box>
        )}

        {filteredMetricItems.map((item) => {
          const { unit, status, value } = item;

          return (
            <Box
              key={`${value}-${unit}-${status}`}
              display='flex'
              gap={1}
              justifyContent='space-between'
              sx={{
                borderBottom: `1px solid ${palette.SlabGray.Stroke}`,
                mb: 1,
              }}
            >
              <Box display='flex' alignItems='baseline' gap={0.5} sx={{ mb: 1 }}>
                <Tooltip
                  title={
                    !Number.isNaN(value) && Number(value) > 1000 ? ValueToNumberString(value) : ''
                  }
                >
                  <Typography
                    variant='h1'
                    sx={{
                      fontSize: '1.8rem',
                    }}
                  >
                    {renderValue(value)}
                  </Typography>
                </Tooltip>

                {hasValue(unit) && !isLoading && (
                  <Typography variant='body1' color='text.secondary' sx={{ fontWeight: 500 }}>
                    {unit}
                  </Typography>
                )}
              </Box>
              {hasValue(status) && (
                <Typography
                  variant='caption'
                  color={getStatusColor(status)}
                  sx={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center' }}
                >
                  {getStatusIcon(status)}{' '}
                  <Box sx={{ ml: 0.5 }} component='span'>
                    {status}
                  </Box>
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </SlabCard>
  );
};
